import useSmoothProgress from "@hooks/useSmoothProgress";
import { getWordMeaning, saveLyricsSyncOffset } from "../../api";
import { IWordMeaningResponse } from "@shared/dto";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { IFoundLyrics } from "@shared/dto";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  adjustSyncOffset,
  adjustedTimestamp,
  getSyncSaveErrorMessage,
  MAX_SYNC_OFFSET_SECONDS,
  saveSyncOffsetWithRetry
} from "./lyricsSync";
import "./LyricsDisplay.css";

interface IProps {
  lyricsDetails: IFoundLyrics;
  artist: string;
  title: string;
  album: string;
  spotifyId: string;
  progressMs: number;
  paused: boolean;
}

const Root = styled("div")(({ theme }) => ({
  margin: "auto",
  maxWidth: 700,
  position: "relative",
  textAlign: "center"
}));

const Lyrics = styled(Typography)(({ theme }) => ({
  fontSize: "clamp(1rem, 1.4vw, 1.2rem)",
  lineHeight: 1.65,
  whiteSpace: "pre-wrap"
}));

const HighlightedLyricsWrapper = styled("div")(({ theme }) => ({
  marginTop: 20,
  marginBottom: 20
}));

const HighlightedLyrics = styled("div")(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, rgba(46, 125, 50, 0.42), rgba(76, 175, 80, 0.12))"
      : "linear-gradient(135deg, rgba(46, 125, 50, 0.2), rgba(76, 175, 80, 0.07))",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(129, 199, 132, 0.34)" : "rgba(46, 125, 50, 0.24)"}`,
  borderRadius: "0.38em",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 14px 38px rgba(0, 0, 0, 0.3), 0 0 34px rgba(76, 175, 80, 0.08)"
      : "0 12px 30px rgba(27, 94, 32, 0.12)",
  boxSizing: "border-box",
  display: "inline-block",
  fontSize: "clamp(2.75rem, 5.6vw, 4.5rem)",
  fontWeight: 700,
  letterSpacing: "-0.025em",
  lineHeight: 1.05,
  maxWidth: "100%",
  padding: "0.22em 0.42em 0.27em",
  textWrap: "balance",
  whiteSpace: "pre-wrap"
}));

const LyricsBlock = styled("div")({
  whiteSpace: "pre-wrap"
});

const SyncOffsetRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  minHeight: 52,
  position: "sticky",
  top: theme.spacing(1.5),
  transform: "translateX(24px)",
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    transform: "none"
  }
}));

const SyncOffsetControl = styled("div")(({ theme }) => ({
  alignItems: "center",
  alignSelf: "flex-start",
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 999,
  columnGap: 8,
  display: "inline-flex",
  opacity: 0.62,
  padding: 4,
  touchAction: "manipulation",
  transition: "opacity 120ms ease",
  "&:focus-within, &:hover": {
    opacity: 1
  },
  "& .MuiIconButton-root": {
    height: 44,
    width: 44
  }
}));

const SyncOffsetValue = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.72rem",
  fontVariantNumeric: "tabular-nums",
  minWidth: 52,
  textAlign: "center"
}));

interface MeaningModalState {
  open: boolean;
  selectedText: string;
  selectionType: "word" | "lyric";
  translation: string;
  loading: boolean;
  syncedLyricIndex: number | null;
}

const TRANSLATION_CACHE_PREFIX = "spotify-panel:translation";

interface TranslationRequest {
  word: string;
  selectionType: "word" | "lyric";
  selectedText: string;
  currentLyric: string;
  previousSentence: string;
  currentSentence: string;
  nextSentence: string;
  syncedLyricIndex: number | null;
}

interface PendingSyncOffset {
  spotifyId: string;
  syncOffsetSeconds: number;
}

const SYNC_OFFSET_SAVE_DEBOUNCE_MS = 750;

const LyricsDisplay: React.FunctionComponent<IProps> = ({
  lyricsDetails,
  artist,
  title,
  spotifyId,
  progressMs,
  paused
}) => {
  const lyricsRef = useRef<HTMLDivElement | null>(null);
  const highlightedRef = useRef<HTMLDivElement | null>(null);
  const meaningRequestIdRef = useRef(0);
  const syncOffsetRef = useRef(lyricsDetails.syncOffsetSeconds ?? 0);
  const activeSpotifyIdRef = useRef(spotifyId);
  const pendingSyncOffsetRef = useRef<PendingSyncOffset | null>(null);
  const syncOffsetTimerRef = useRef<number | null>(null);
  const syncOffsetRequestIdRef = useRef(0);
  const [syncEnabled] = useState(true);
  const [syncOffsetSeconds, setSyncOffsetSeconds] = useState(lyricsDetails.syncOffsetSeconds ?? 0);
  const [meaningModal, setMeaningModal] = useState<MeaningModalState>({
    open: false,
    selectedText: "",
    selectionType: "word",
    translation: "",
    loading: false,
    syncedLyricIndex: null
  });

  const { progress: smoothedProgressMs } = useSmoothProgress(
    progressMs,
    Infinity,
    !paused,
    null,
    250
  );

  useEffect(() => {
    activeSpotifyIdRef.current = spotifyId;
    syncOffsetRequestIdRef.current += 1;
    const initialOffset = lyricsDetails.syncOffsetSeconds ?? 0;
    syncOffsetRef.current = initialOffset;
    setSyncOffsetSeconds(initialOffset);
    pendingSyncOffsetRef.current = null;

    return () => {
      if (syncOffsetTimerRef.current !== null) {
        window.clearTimeout(syncOffsetTimerRef.current);
        syncOffsetTimerRef.current = null;
      }

      const pending = pendingSyncOffsetRef.current;
      pendingSyncOffsetRef.current = null;
      syncOffsetRequestIdRef.current += 1;
      if (pending !== null) {
        void saveSyncOffsetWithRetry(() =>
          saveLyricsSyncOffset(pending.spotifyId, pending.syncOffsetSeconds)
        ).catch(() => {
          // The display is leaving this track, so there is no current UI to update.
        });
      }
    };
  }, [lyricsDetails.syncOffsetSeconds, spotifyId]);

  // Automatically scroll to highlighted text
  useEffect(() => {
    const element = highlightedRef.current;
    if (syncEnabled && element !== null) {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
  }, [syncEnabled, smoothedProgressMs]);

  const lyricsState = useMemo(
    () =>
      calculateLyricsState(
        lyricsDetails,
        smoothedProgressMs,
        syncEnabled,
        paused,
        syncOffsetSeconds
      ),
    [lyricsDetails, smoothedProgressMs, syncEnabled, paused, syncOffsetSeconds]
  );
  const sentenceContext = useMemo(
    () => extractSentences(getLyricsText(lyricsDetails)),
    [lyricsDetails]
  );
  const structuredLyrics = useMemo(() => buildStructuredLyrics(lyricsState), [lyricsState]);

  const requestTranslation = async (request: TranslationRequest) => {
    const requestId = ++meaningRequestIdRef.current;

    setMeaningModal({
      open: true,
      selectedText: request.selectedText,
      selectionType: request.selectionType,
      translation: "",
      loading: true,
      syncedLyricIndex: request.syncedLyricIndex
    });

    if (request.selectedText.trim() === "") {
      setMeaningModal(state => ({
        ...state,
        translation: "No lyric text on this synced line.",
        loading: false
      }));
      return;
    }

    const cacheKey = getTranslationCacheKey(
      spotifyId,
      artist,
      title,
      request.selectionType,
      request.selectedText
    );
    const cachedTranslation = getCachedTranslation(cacheKey);

    if (cachedTranslation !== null) {
      setMeaningModal(state => ({
        ...state,
        translation: cachedTranslation,
        loading: false
      }));
      return;
    }

    try {
      const response: IWordMeaningResponse = await getWordMeaning({
        artist,
        title,
        word: request.word,
        selectionType: request.selectionType,
        selectedText: request.selectedText,
        currentLyric: request.currentLyric,
        previousSentence: request.previousSentence,
        currentSentence: request.currentSentence,
        nextSentence: request.nextSentence
      });

      setCachedTranslation(cacheKey, response.translation);
      if (meaningRequestIdRef.current !== requestId) return;

      setMeaningModal(state => ({
        ...state,
        selectedText: response.selectedText,
        selectionType: response.selectionType,
        translation: response.translation,
        loading: false
      }));
    } catch (_error) {
      if (meaningRequestIdRef.current !== requestId) return;

      setMeaningModal(state => ({
        ...state,
        translation:
          request.selectionType === "lyric"
            ? "Cannot load lyric translation"
            : "Cannot load word translation",
        loading: false
      }));
    }
  };

  const handleWordClick = (token: WordToken) => {
    const isHighlightedToken = token.section === "highlighted";
    const selectedText = isHighlightedToken
      ? lyricsState.highlighted || token.lineText
      : token.value;
    const selectionType: "word" | "lyric" = isHighlightedToken ? "lyric" : "word";
    const syncedLyricIndex = token.syncedLyricIndex;
    const syncedLyrics = lyricsDetails.syncedLyrics ?? [];

    void requestTranslation({
      word: token.value,
      selectionType,
      selectedText,
      currentLyric: isHighlightedToken ? selectedText : token.lineText,
      previousSentence:
        syncedLyricIndex !== null
          ? (syncedLyrics[syncedLyricIndex - 1]?.content ?? "")
          : (sentenceContext[token.sentenceIndex - 1] ?? ""),
      currentSentence: token.lineText || sentenceContext[token.sentenceIndex] || "",
      nextSentence:
        syncedLyricIndex !== null
          ? (syncedLyrics[syncedLyricIndex + 1]?.content ?? "")
          : (sentenceContext[token.sentenceIndex + 1] ?? ""),
      syncedLyricIndex
    });
  };

  const navigateSyncedLyric = (offset: number) => {
    const syncedLyrics = lyricsDetails.syncedLyrics;
    const currentIndex = meaningModal.syncedLyricIndex;
    if (!syncedLyrics || currentIndex === null) return;

    const nextIndex = currentIndex + offset;
    const lyric = syncedLyrics[nextIndex];
    if (!lyric) return;

    void requestTranslation({
      word: lyric.content,
      selectionType: "lyric",
      selectedText: lyric.content,
      currentLyric: lyric.content,
      previousSentence: syncedLyrics[nextIndex - 1]?.content ?? "",
      currentSentence: lyric.content,
      nextSentence: syncedLyrics[nextIndex + 1]?.content ?? "",
      syncedLyricIndex: nextIndex
    });
  };

  const savePendingSyncOffset = async (pending: PendingSyncOffset) => {
    const requestId = ++syncOffsetRequestIdRef.current;

    try {
      await saveSyncOffsetWithRetry(() =>
        saveLyricsSyncOffset(pending.spotifyId, pending.syncOffsetSeconds)
      );

      if (
        requestId === syncOffsetRequestIdRef.current &&
        activeSpotifyIdRef.current === pending.spotifyId &&
        pendingSyncOffsetRef.current?.syncOffsetSeconds === pending.syncOffsetSeconds
      ) {
        pendingSyncOffsetRef.current = null;
      }
    } catch (error) {
      if (
        requestId === syncOffsetRequestIdRef.current &&
        activeSpotifyIdRef.current === pending.spotifyId &&
        pendingSyncOffsetRef.current?.syncOffsetSeconds === pending.syncOffsetSeconds
      ) {
        pendingSyncOffsetRef.current = null;
        toast.error(getSyncSaveErrorMessage(error), {
          position: "bottom-center",
          autoClose: 3000
        });
      }
    }
  };

  const scheduleSyncOffsetSave = (nextOffset: number) => {
    const pending = { spotifyId, syncOffsetSeconds: nextOffset };
    pendingSyncOffsetRef.current = pending;

    if (syncOffsetTimerRef.current !== null) {
      window.clearTimeout(syncOffsetTimerRef.current);
    }

    syncOffsetTimerRef.current = window.setTimeout(() => {
      syncOffsetTimerRef.current = null;
      const latestPending = pendingSyncOffsetRef.current;
      if (latestPending !== null) {
        void savePendingSyncOffset(latestPending);
      }
    }, SYNC_OFFSET_SAVE_DEBOUNCE_MS);
  };

  const changeSyncOffset = (direction: -1 | 1) => {
    const nextOffset = adjustSyncOffset(syncOffsetRef.current, direction);
    if (nextOffset === syncOffsetRef.current) return;

    syncOffsetRef.current = nextOffset;
    setSyncOffsetSeconds(nextOffset);
    scheduleSyncOffsetSave(nextOffset);
  };

  return (
    <Root>
      {lyricsDetails.syncedLyrics !== null && lyricsDetails.syncedLyrics.length > 0 && (
        <SyncOffsetRow>
          <SyncOffsetControl aria-label="Lyrics synchronization offset">
            <Tooltip title="Show all lyrics 0.5 seconds later">
              <span>
                <IconButton
                  aria-label="Show all lyrics 0.5 seconds later"
                  disabled={syncOffsetSeconds <= -MAX_SYNC_OFFSET_SECONDS}
                  onClick={() => changeSyncOffset(-1)}
                  size="small"
                >
                  <RemoveIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
            <SyncOffsetValue>{formatSyncOffset(syncOffsetSeconds)}</SyncOffsetValue>
            <Tooltip title="Show all lyrics 0.5 seconds sooner">
              <span>
                <IconButton
                  aria-label="Show all lyrics 0.5 seconds sooner"
                  disabled={syncOffsetSeconds >= MAX_SYNC_OFFSET_SECONDS}
                  onClick={() => changeSyncOffset(1)}
                  size="small"
                >
                  <AddIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          </SyncOffsetControl>
        </SyncOffsetRow>
      )}
      <Lyrics ref={lyricsRef}>
        <LyricsBlock>{renderLines(structuredLyrics.before, handleWordClick)}</LyricsBlock>

        {structuredLyrics.highlighted.length > 0 && (
          <HighlightedLyricsWrapper>
            <HighlightedLyrics ref={highlightedRef}>
              <LyricsBlock>
                {renderLines(structuredLyrics.highlighted, handleWordClick)}
              </LyricsBlock>
            </HighlightedLyrics>
          </HighlightedLyricsWrapper>
        )}

        <LyricsBlock>{renderLines(structuredLyrics.after, handleWordClick)}</LyricsBlock>
      </Lyrics>

      {meaningModal.open && (
        <Box
          className="meaning-overlay"
          onClick={() => setMeaningModal(state => ({ ...state, open: false }))}
        >
          <Box className="meaning-card" onClick={event => event.stopPropagation()}>
            <span className="meaning-chip">
              {meaningModal.selectionType === "lyric" ? "Lyric Translation" : "Word Meaning"}
            </span>
            <span className="meaning-word">{meaningModal.selectedText}</span>
            {meaningModal.loading ? (
              <CircularProgress size={28} sx={{ color: "#ffc766" }} />
            ) : (
              <div className="meaning-section">
                <span className="meaning-section-label">
                  {meaningModal.selectionType === "lyric"
                    ? "Selected Lyric Translation"
                    : "Selected Word Translation"}
                </span>
                <p className="meaning-body">{meaningModal.translation}</p>
              </div>
            )}
            {meaningModal.syncedLyricIndex !== null && lyricsDetails.syncedLyrics && (
              <div className="meaning-navigation">
                <Tooltip title="Previous synced lyric">
                  <span>
                    <IconButton
                      aria-label="Translate previous synced lyric"
                      disabled={meaningModal.loading || meaningModal.syncedLyricIndex === 0}
                      onClick={() => navigateSyncedLyric(-1)}
                      size="large"
                    >
                      <ArrowBackIosNewIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <span className="meaning-navigation-position">
                  {meaningModal.syncedLyricIndex + 1} / {lyricsDetails.syncedLyrics.length}
                </span>
                <Tooltip title="Next synced lyric">
                  <span>
                    <IconButton
                      aria-label="Translate next synced lyric"
                      disabled={
                        meaningModal.loading ||
                        meaningModal.syncedLyricIndex === lyricsDetails.syncedLyrics.length - 1
                      }
                      onClick={() => navigateSyncedLyric(1)}
                      size="large"
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            )}
            <span className="meaning-hint">Tap outside to close</span>
          </Box>
        </Box>
      )}
    </Root>
  );
};

const calculateLyricsState = (
  lyricsDetails: IFoundLyrics,
  progressMs: number,
  syncEnabled: boolean,
  paused: boolean,
  syncOffsetSeconds: number
) => {
  const progressSeconds = progressMs / 1000;

  // If there is no syncedLyricsArray or sync is disabled or the song is paused, return the plain lyrics
  if (lyricsDetails.syncedLyrics === null || !syncEnabled || paused) {
    return {
      before: "",
      highlighted: "",
      after: lyricsDetails.plainLyrics ?? "",
      beforeIndexes: [],
      highlightedIndexes: [],
      afterIndexes: []
    };
  }

  // Calculate the current lyric state based on progress
  const passedLyricsAndCurrent =
    lyricsDetails.syncedLyrics.filter(
      x => adjustedTimestamp(x.timestamp, syncOffsetSeconds) <= progressSeconds
    ) ?? [];
  const highlightedIndex = passedLyricsAndCurrent.length - 1;
  const passedLyrics = passedLyricsAndCurrent.slice(0, -1);
  const currentLyrics =
    passedLyricsAndCurrent.length > 0
      ? passedLyricsAndCurrent[passedLyricsAndCurrent.length - 1]
      : null;
  const upcomingLyrics = lyricsDetails.syncedLyrics.filter(
    x => adjustedTimestamp(x.timestamp, syncOffsetSeconds) > progressSeconds
  );

  return {
    before: passedLyrics.map(x => x.content).join(" \n "),
    highlighted: currentLyrics?.content ?? "",
    after: upcomingLyrics.map(x => x.content).join(" \n "),
    beforeIndexes: passedLyrics.map((_, index) => index),
    highlightedIndexes: highlightedIndex >= 0 ? [highlightedIndex] : [],
    afterIndexes: upcomingLyrics.map((_, index) => highlightedIndex + 1 + index)
  };
};

function formatSyncOffset(syncOffsetSeconds: number): string {
  const sign = syncOffsetSeconds > 0 ? "+" : "";
  return `${sign}${syncOffsetSeconds.toFixed(1)}s`;
}

interface LyricsState {
  before: string;
  highlighted: string;
  after: string;
  beforeIndexes: number[];
  highlightedIndexes: number[];
  afterIndexes: number[];
}

interface StructuredLyrics {
  before: RenderLine[];
  highlighted: RenderLine[];
  after: RenderLine[];
}

interface RenderLine {
  id: string;
  tokens: RenderToken[];
}

interface BaseToken {
  id: string;
  value: string;
}

interface TextToken extends BaseToken {
  type: "text";
}

interface WordToken extends BaseToken {
  type: "word";
  sentenceIndex: number;
  lineText: string;
  section: "before" | "highlighted" | "after";
  syncedLyricIndex: number | null;
}

type RenderToken = TextToken | WordToken;

function buildStructuredLyrics(lyricsState: LyricsState): StructuredLyrics {
  const state = {
    sentenceIndex: 0,
    tokenIndex: 0,
    lineIndex: 0
  };

  return {
    before: tokenizeLines(
      splitLines(lyricsState.before),
      state,
      "before",
      lyricsState.beforeIndexes
    ),
    highlighted: tokenizeLines(
      splitLines(lyricsState.highlighted),
      state,
      "highlighted",
      lyricsState.highlightedIndexes
    ),
    after: tokenizeLines(splitLines(lyricsState.after), state, "after", lyricsState.afterIndexes)
  };
}

function splitLines(value: string): string[] {
  if (value === "") {
    return [];
  }
  return value.split("\n");
}

function tokenizeLines(
  lines: string[],
  state: { sentenceIndex: number; tokenIndex: number; lineIndex: number },
  prefix: "before" | "highlighted" | "after",
  syncedLyricIndexes: number[]
): RenderLine[] {
  return lines.map((line, index) => ({
    id: `${prefix}-${state.lineIndex++}`,
    tokens: tokenizeLine(line, state, prefix, syncedLyricIndexes[index] ?? null)
  }));
}

function tokenizeLine(
  line: string,
  state: { sentenceIndex: number; tokenIndex: number },
  section: "before" | "highlighted" | "after",
  syncedLyricIndex: number | null
): RenderToken[] {
  const tokens: RenderToken[] = [];
  const matches = Array.from(
    line.matchAll(/[^\s.,!?;:()[\]{}"“”]+(?:['’-][^\s.,!?;:()[\]{}"“”]+)*/g)
  );

  if (matches.length === 0) {
    if (line !== "") {
      tokens.push({
        id: `text-${state.tokenIndex++}`,
        type: "text",
        value: line
      });
    }
    return tokens;
  }

  let cursor = 0;
  matches.forEach((match, index) => {
    const startIndex = match.index ?? 0;
    const nextStartIndex = matches[index + 1]?.index ?? line.length;
    const word = match[0];
    const wordEndIndex = startIndex + word.length;

    if (startIndex > cursor) {
      tokens.push({
        id: `text-${state.tokenIndex++}`,
        type: "text",
        value: line.slice(cursor, startIndex)
      });
    }

    tokens.push({
      id: `word-${state.tokenIndex++}`,
      type: "word",
      value: word,
      sentenceIndex: state.sentenceIndex,
      lineText: line.trim(),
      section,
      syncedLyricIndex
    });

    if (nextStartIndex > wordEndIndex) {
      const trailing = line.slice(wordEndIndex, nextStartIndex);
      tokens.push({
        id: `text-${state.tokenIndex++}`,
        type: "text",
        value: trailing
      });

      if (/[.!?](?=(?:["')\]]|\s|$))/.test(trailing)) {
        state.sentenceIndex += 1;
      }
    }

    cursor = nextStartIndex;
  });

  return tokens;
}

function renderLines(lines: RenderLine[], onWordClick: (token: WordToken) => void) {
  return lines.map(line => (
    <span className="lyrics-line" key={line.id}>
      {line.tokens.length === 0 ? "\u00A0" : renderTokens(line.tokens, onWordClick)}
    </span>
  ));
}

function renderTokens(tokens: RenderToken[], onWordClick: (token: WordToken) => void) {
  return tokens.map(token => {
    if (token.type === "word") {
      return (
        <span
          className="lyrics-word"
          key={token.id}
          onClick={() => onWordClick(token)}
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onWordClick(token);
            }
          }}
        >
          {token.value}
        </span>
      );
    }

    return <React.Fragment key={token.id}>{token.value}</React.Fragment>;
  });
}

function getLyricsText(lyricsDetails: IFoundLyrics): string {
  if (lyricsDetails.plainLyrics && lyricsDetails.plainLyrics.trim() !== "") {
    return lyricsDetails.plainLyrics;
  }

  if (lyricsDetails.syncedLyrics) {
    return lyricsDetails.syncedLyrics.map(line => line.content).join("\n");
  }

  return "";
}

function extractSentences(text: string): string[] {
  const normalizedText = text.replace(/\s+/g, " ").trim();
  if (normalizedText === "") {
    return [];
  }

  const matches = normalizedText.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g);
  if (!matches) {
    return [normalizedText];
  }

  return matches.map(sentence => sentence.trim()).filter(Boolean);
}

function getTranslationCacheKey(
  spotifyId: string,
  artist: string,
  title: string,
  selectionType: "word" | "lyric",
  selectedText: string
): string {
  const songKey = spotifyId || `${artist.trim().toLowerCase()}::${title.trim().toLowerCase()}`;
  const normalizedText = selectedText.trim().toLowerCase().replace(/\s+/g, " ");
  return JSON.stringify([
    TRANSLATION_CACHE_PREFIX,
    songKey,
    selectionType,
    "PT-BR",
    normalizedText
  ]);
}

function getCachedTranslation(cacheKey: string): string | null {
  try {
    return window.sessionStorage.getItem(cacheKey);
  } catch (_error) {
    return null;
  }
}

function setCachedTranslation(cacheKey: string, translation: string): void {
  try {
    window.sessionStorage.setItem(cacheKey, translation);
  } catch (_error) {
    // Translation still works when storage is unavailable or full.
  }
}

export default LyricsDisplay;
